package com.rajen.angrejidictionary.data.remote

import com.rajen.angrejidictionary.data.local.AdminAuthManager
import com.rajen.angrejidictionary.data.remote.dto.*
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json

object AdminApi {

    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
            })
        }
    }

    suspend fun login(
        request: AdminLoginRequest
    ): AdminApiResponse<AdminLoginResponse> {
        return client.post(ApiConfig.ADMIN_LOGIN_ENDPOINT) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()
    }

    suspend fun logout(): AdminApiResponse<Unit> {
        val token = AdminAuthManager.getToken()
            ?: return AdminApiResponse(success = true, data = Unit, error = null)

        return try {
            client.post(ApiConfig.ADMIN_LOGOUT_ENDPOINT) {
                header(HttpHeaders.Authorization, "Bearer $token")
            }.body()
        } catch (e: Exception) {
            AdminApiResponse(success = true, data = Unit, error = null)
        }
    }

    suspend fun createWord(
        request: CreateWordRequest
    ): AdminApiResponse<String> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.post(ApiConfig.ADMIN_WORDS_ENDPOINT) {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $token")
            setBody(request)
        }.body()
    }

    suspend fun getWords(
        page: Int = 1,
        pageSize: Int = 20
    ): AdminApiResponse<AdminWordListResponse> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.get(ApiConfig.ADMIN_WORDS_ENDPOINT) {
            header(HttpHeaders.Authorization, "Bearer $token")
            url {
                parameters.append("page", page.toString())
                parameters.append("pageSize", pageSize.toString())
            }
        }.body()
    }

    // ── Word edit / delete ──────────────────────────────────────────
    // GET    /api/v1/admin/words/{id} -> full Item (for prefilling the edit form)
    // PUT    /api/v1/admin/words/{id} -> full updated Item
    // DELETE /api/v1/admin/words/{id} -> true

    suspend fun getWordDetail(id: String): AdminApiResponse<ItemDto> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.get("${ApiConfig.ADMIN_WORDS_ENDPOINT}/$id") {
            header(HttpHeaders.Authorization, "Bearer $token")
        }.body()
    }

    suspend fun updateWord(
        id: String,
        request: CreateWordRequest
    ): AdminApiResponse<ItemDto> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.put("${ApiConfig.ADMIN_WORDS_ENDPOINT}/$id") {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $token")
            setBody(request)
        }.body()
    }

    suspend fun deleteWord(id: String): AdminApiResponse<Boolean> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.delete("${ApiConfig.ADMIN_WORDS_ENDPOINT}/$id") {
            header(HttpHeaders.Authorization, "Bearer $token")
        }.body()
    }

    // ── Word of the Day ─────────────────────────────────────────────
    // GET /api/v1/admin/word-of-day -> the pinned word's id, or no `data` at all if unpinned
    // PUT /api/v1/admin/word-of-day -> { word_id } to pin, { word_id: null } to clear

    suspend fun getWordOfDay(): AdminApiResponse<String> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.get(ApiConfig.ADMIN_WORD_OF_DAY_ENDPOINT) {
            header(HttpHeaders.Authorization, "Bearer $token")
        }.body()
    }

    suspend fun setWordOfDay(wordId: String?): AdminApiResponse<Boolean> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.put(ApiConfig.ADMIN_WORD_OF_DAY_ENDPOINT) {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $token")
            setBody(WordOfDayRequest(wordId))
        }.body()
    }

    suspend fun getSuggestions(
        page: Int = 1,
        pageSize: Int = 20,
        status: String? = null
    ): AdminApiResponse<SuggestionListData> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.get(ApiConfig.ADMIN_SUGGESTIONS_ENDPOINT) {
            header(HttpHeaders.Authorization, "Bearer $token")
            url {
                parameters.append("page", page.toString())
                parameters.append("pageSize", pageSize.toString())
                if (status != null) {
                    parameters.append("status", status)
                }
            }
        }.body()
    }

    suspend fun updateSuggestionStatus(
        id: String,
        status: String
    ): AdminApiResponse<Boolean> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.patch("${ApiConfig.ADMIN_SUGGESTIONS_ENDPOINT}/$id/status") {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $token")
            setBody(
                mapOf(
                    "status" to status,
                    "reviewedBy" to (AdminAuthManager.getUsername() ?: "admin")
                )
            )
        }.body()
    }

    suspend fun deleteSuggestion(id: String): AdminApiResponse<Boolean> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.delete("${ApiConfig.ADMIN_SUGGESTIONS_ENDPOINT}/$id") {
            header(HttpHeaders.Authorization, "Bearer $token")
        }.body()
    }

    // ── Push notifications ────────────────────────────────────────
    // POST /api/v1/admin/notifications/broadcast -> send to every registered device token

    suspend fun sendBroadcastNotification(
        request: BroadcastNotificationRequest
    ): AdminApiResponse<BroadcastNotificationResult> {
        val token = AdminAuthManager.getToken()
            ?: throw IllegalStateException("Not authenticated")

        return client.post(ApiConfig.ADMIN_BROADCAST_ENDPOINT) {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $token")
            setBody(request)
        }.body()
    }
}
